// src/screens/MonEquipe.jsx
// Le créateur invite des travailleurs qui pourront voir SES événements
// et scanner les billets (mais pas créer/modifier d'événements).

import React, { useCallback, useEffect, useState } from "react";
import { base_url } from "../utils/constante";

function authHeaders() {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    return {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
    };
}

export default function MonEquipe() {
    const [travailleurs, setTravailleurs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [erreur, setErreur] = useState(null);

    // Modal invitation
    const [modalOuvert, setModalOuvert] = useState(false);
    const [nomComplet, setNomComplet] = useState("");
    const [email, setEmail] = useState("");
    const [telephone, setTelephone] = useState("");
    const [envoi, setEnvoi] = useState(false);

    // Affichage du mot de passe temporaire généré
    const [dernierMotDePasse, setDernierMotDePasse] = useState(null);

    const charger = useCallback(async () => {
        setLoading(true);
        setErreur(null);
        try {
            const res = await fetch(`${base_url}createur/equipe`, {
                method: "POST",
                headers: authHeaders(),
            });
            if (!res.ok) throw new Error("Erreur lors du chargement de l'équipe");
            const data = await res.json();
            setTravailleurs(data.travailleurs || []);
        } catch (e) {
            setErreur(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { charger(); }, [charger]);

    async function inviter() {
        if (!nomComplet.trim() || !email.trim() || !telephone.trim()) {
            alert("Tous les champs sont obligatoires.");
            return;
        }
        setEnvoi(true);
        try {
            const res = await fetch(`${base_url}createur/equipe/inviter`, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify({ nomComplet, email, telephone }),
            });
            const data = await res.json();
            if (!res.ok) {
                alert(data.message || "Erreur lors de l'invitation");
                return;
            }
            setDernierMotDePasse(data.mot_de_passe_temporaire);
            setNomComplet(""); setEmail(""); setTelephone("");
            charger();
        } catch (e) {
            alert("Erreur de connexion");
        } finally {
            setEnvoi(false);
        }
    }

    async function revoquer(id, nom) {
        if (!window.confirm(`Supprimer définitivement le compte de ${nom} ?\n\nCette action est irréversible : le compte et son accès seront totalement supprimés.`)) return;
        try {
            const res = await fetch(`${base_url}createur/equipe/revoquer`, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify({ id }),
            });
            const data = await res.json();
            if (!res.ok) { alert(data.message || "Erreur"); return; }
            setTravailleurs((prev) => prev.filter((t) => t.id !== id));
        } catch (e) {
            alert("Erreur de connexion");
        }
    }

    // ── Réinitialiser le mot de passe d'un travailleur ──────
    async function reinitialiserMotDePasse(id, nom) {
        if (!window.confirm(`Générer un nouveau mot de passe pour ${nom} ?`)) return;
        try {
            const res = await fetch(`${base_url}createur/equipe/reinitialiser-password`, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify({ id }),
            });
            const data = await res.json();
            if (!res.ok) { alert(data.message || "Erreur"); return; }
            setDernierMotDePasse(data.nouveau_mot_de_passe);
            setModalOuvert(true); // réutilise la modal pour afficher le mot de passe
        } catch (e) {
            alert("Erreur de connexion");
        }
    }

    return (
        <div className="w-full flex justify-center bg-white min-h-screen">
            <div className="w-full max-w-3xl px-4 lg:px-0 py-8">

                <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
                    <p className="text-sm text-gray-400">
                        Les membres de votre équipe peuvent consulter vos événements et
                        scanner les billets à l'entrée. Ils ne peuvent pas créer ni
                        modifier d'événements.
                    </p>
                    <button
                        onClick={() => setModalOuvert(true)}
                        className="px-4 py-2 rounded-xl text-white text-sm font-semibold whitespace-nowrap"
                        style={{ backgroundColor: "#481699" }}
                    >
                        + Inviter un membre
                    </button>
                </div>

                {erreur && (
                    <div className="bg-red-50 text-red-600 rounded-xl p-4 my-4 border border-red-200">
                        {erreur}
                    </div>
                )}

                {loading ? (
                    <div className="text-gray-400 text-center py-20">Chargement…</div>
                ) : travailleurs.length === 0 ? (
                    <div className="text-gray-400 text-center py-20 bg-white rounded-2xl border border-gray-200 mt-4">
                        Aucun membre dans votre équipe pour le moment.
                    </div>
                ) : (
                    <div className="flex flex-col gap-3 mt-4">
                        {travailleurs.map((t) => (
                            <div
                                key={t.id}
                                className="w-full bg-white rounded-2xl border border-gray-200 shadow-sm p-4 flex items-center gap-4"
                            >
                                <img
                                    src={t.photo_profil}
                                    alt=""
                                    className="w-12 h-12 rounded-full object-cover shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-gray-800 truncate">{t.nomComplet}</div>
                                    <div className="text-sm text-gray-400 truncate">{t.email}</div>
                                    <div className="text-xs text-gray-400">{t.telephone}</div>
                                </div>
                                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-50" style={{ color: "#481699" }}>
                                    Scanner
                                </span>
                                <button
                                    onClick={() => reinitialiserMotDePasse(t.id, t.nomComplet)}
                                    className="text-sm font-medium hover:opacity-70 transition shrink-0"
                                    style={{ color: "#481699" }}
                                >
                                    🔑 Mot de passe
                                </button>
                                <button
                                    onClick={() => revoquer(t.id, t.nomComplet)}
                                    className="text-sm font-medium text-red-500 hover:text-red-700 transition shrink-0"
                                >
                                    Supprimer
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* ── MODAL INVITATION ── */}
            {modalOuvert && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
                        <h2 className="font-bold text-lg mb-1 text-gray-800">
                            Inviter un membre de l'équipe
                        </h2>
                        <p className="text-gray-400 text-sm mb-4">
                            Il recevra un accès pour scanner vos billets.
                        </p>

                        {dernierMotDePasse ? (
                            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                                <p className="text-green-700 text-sm font-medium mb-2">
                                    ✓ Fait !
                                </p>
                                <p className="text-sm text-gray-600 mb-1">
                                    Transmettez ce mot de passe au membre concerné :
                                </p>
                                <p className="text-sm font-mono bg-white rounded-lg px-3 py-2 border border-gray-200">
                                    Mot de passe : <strong>{dernierMotDePasse}</strong>
                                </p>
                                <button
                                    onClick={() => { setDernierMotDePasse(null); setModalOuvert(false); }}
                                    className="mt-4 w-full px-4 py-2 rounded-xl text-white text-sm font-semibold"
                                    style={{ backgroundColor: "#481699" }}
                                >
                                    Fermer
                                </button>
                            </div>
                        ) : (
                            <>
                                <label className="font-semibold text-sm text-gray-700 mb-1 block">Nom complet</label>
                                <input
                                    value={nomComplet}
                                    onChange={(e) => setNomComplet(e.target.value)}
                                    placeholder="Ex : Marie Dupont"
                                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-gray-800 text-sm mb-3 focus:outline-none focus:border-color1"
                                />

                                <label className="font-semibold text-sm text-gray-700 mb-1 block">Email</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="marie@exemple.fr"
                                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-gray-800 text-sm mb-3 focus:outline-none focus:border-color1"
                                />

                                <label className="font-semibold text-sm text-gray-700 mb-1 block">Téléphone</label>
                                <input
                                    value={telephone}
                                    onChange={(e) => setTelephone(e.target.value)}
                                    placeholder="06 12 34 56 78"
                                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-gray-800 text-sm mb-4 focus:outline-none focus:border-color1"
                                />

                                <div className="flex gap-3 justify-end">
                                    <button
                                        onClick={() => setModalOuvert(false)}
                                        className="px-4 py-2 rounded-xl text-gray-500 hover:text-gray-800 text-sm transition"
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        onClick={inviter}
                                        disabled={envoi}
                                        className="px-4 py-2 rounded-xl text-white text-sm font-semibold disabled:opacity-50"
                                        style={{ backgroundColor: "#481699" }}
                                    >
                                        {envoi ? "Envoi…" : "Inviter"}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}